<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useTranslation } from "i18next-vue";
import DrawerMenuContent from "@/components/canvas/DrawerMenus/DrawerMenuContent.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { useGetDrawingBranches } from "@/stores/queries/drawings.query";
import { useDeviceState } from "@/stores/DeviceState";
import { mergeSideCss, useBranchMergeState } from "@/stores/BranchMergeState";
import { drawingRepository } from "@/repositories/drawing.repository";
import { useDates } from "@/composable/useDates";
import { trackAction } from "@/analytics/drawing-analytics";
import { entityKindLabel } from "@/utils/branch-merge/labels";
import type { DRBranchWithContributors, DRDrawing } from "@/types";
import type {
  ConflictChoice,
  MergeConflict,
  NamedRecordKind,
} from "@/utils/branch-merge/types";

/**
 * Volet de fusion de deux versions du plan, ouvert depuis le menu du fichier :
 * choix des versions, analyse, résolution des conflits d'entités un par un —
 * le conflit courant est montré sur le plan (fantômes + cadre, cf.
 * MergeConflictOverlay) — puis fusion. La version conservée reçoit la révision
 * fusionnée ; l'autre est absorbée (plus personne n'écrit dessus).
 */

const props = defineProps<{ drawingId: number }>();
const emit = defineEmits<{ (e: "close"): void }>();

const { t } = useTranslation();
const { formatDistanceToNow } = useDates();
const deviceState = useDeviceState();
const drawingId = computed(() => props.drawingId);
const drawing = ref<DRDrawing>();
const { data: branches, isLoading } = useGetDrawingBranches(drawingId);
const merge = useBranchMergeState();
const {
  step,
  errorKey,
  analysis,
  readProgress,
  resolutions,
  remaining,
  result,
  conflicts,
  currentConflictId,
  currentIndex,
} = storeToRefs(merge);

onMounted(async () => {
  drawing.value = await drawingRepository.getById(props.drawingId);
});

/** Versions encore éditables (une version absorbée ne se refusionne pas). */
const activeBranches = computed<DRBranchWithContributors[]>(() =>
  (branches.value ?? []).filter(
    (b) => (b.status ?? "ACTIVE") === "ACTIVE" && !!b.headRevisionId,
  ),
);

const targetId = ref<number>();
const mergedId = ref<number>();

watch(
  activeBranches,
  (list) => {
    if (targetId.value === undefined && list.length) {
      const current = drawing.value?.lastRevision.branchId;
      targetId.value = list.some((b) => b.id === current)
        ? (current as number)
        : list[0].id;
    }
    if (mergedId.value === undefined || mergedId.value === targetId.value) {
      mergedId.value = list.find((b) => b.id !== targetId.value)?.id;
    }
  },
  { immediate: true },
);

// Choisir comme version conservée celle qui était à fusionner : on échange.
watch(targetId, (id, previous) => {
  if (id !== undefined && mergedId.value === id) {
    mergedId.value =
      previous ?? activeBranches.value.find((b) => b.id !== id)?.id;
  }
});

// Parmi TOUTES les versions : après la fusion, la version absorbée n'est plus
// active mais son nom reste affiché.
const target = computed(() =>
  branches.value?.find((b) => b.id === targetId.value),
);
const merged = computed(() =>
  branches.value?.find((b) => b.id === mergedId.value),
);
const targetName = computed(() => target.value?.name ?? "");
const mergedName = computed(() => merged.value?.name ?? "");

/**
 * Dernière modification d'une version (« il y a 3 jours · François ») : deux
 * versions portent souvent le même nom (« Version de François »).
 */
function branchDetail(b: DRBranchWithContributors | undefined): string {
  const head = b?.headRevision;
  if (!head) return "";
  return `${formatDistanceToNow(head.editedAt ?? head.savedAt)} · ${head.author.full_name}`;
}

const canAnalyze = computed(
  () =>
    !!target.value &&
    !!merged.value &&
    target.value.id !== merged.value.id &&
    deviceState.isOnline !== false,
);

function startAnalysis() {
  if (!drawing.value || !target.value || !merged.value) return;
  trackAction("merge_versions_analyze");
  void merge.analyze(drawing.value, target.value, merged.value);
}

function startMerge() {
  trackAction("merge_versions_apply");
  void merge.apply();
}

/** N° affichés dans l'alerte : le compte, lui, est exact. */
const MAX_LISTED_MARKERS = 10;

const sharedMarkers = computed(() => analysis.value?.sharedMarkerNumbers ?? []);
const sharedMarkerList = computed(() => {
  const listed = sharedMarkers.value.slice(0, MAX_LISTED_MARKERS).join(", ");
  return sharedMarkers.value.length > MAX_LISTED_MARKERS
    ? `${listed}…`
    : listed;
});

const summary = computed(() => analysis.value?.summary);
const recordSummary = computed(
  () =>
    Object.entries(summary.value?.records ?? {}) as [NamedRecordKind, number][],
);

/** « Ligne », « Texte "Fissure 3" », « Bloc "REP" »… */
function conflictTitle(c: MergeConflict): string {
  const e = c.target ?? c.merged ?? c.base;
  if (!e) return "";
  const kind = t(`canvas:merge.entityKind.${entityKindLabel(e.className)}`);
  const name = e.blockName ?? e.label?.trim();
  return name ? `${kind} « ${name} »` : kind;
}

/** La nature du conflit, avec les noms des versions. */
function conflictSentence(c: MergeConflict): string {
  return t(`canvas:merge.conflict.${c.kind}`, {
    target: targetName.value,
    merged: mergedName.value,
  });
}

function choicesOf(c: MergeConflict): ConflictChoice[] {
  return c.kind === "modify-modify"
    ? ["target", "merged", "both"]
    : ["target", "merged"];
}

/** Version dont le choix porte la couleur (sur le plan et dans le volet). */
function sideOf(choice: ConflictChoice): "target" | "merged" | null {
  return choice === "both" ? null : choice;
}

function choiceLabel(c: MergeConflict, choice: ConflictChoice): string {
  if (choice === "both") return t("canvas:merge.choice.both");
  const name = choice === "target" ? targetName.value : mergedName.value;
  // Face à une suppression, « garder » une version peut vouloir dire supprimer.
  const deleted =
    (c.kind === "modify-delete" && choice === "merged") ||
    (c.kind === "delete-modify" && choice === "target");
  return deleted
    ? t("canvas:merge.choice.deleteAs", { name })
    : t("canvas:merge.choice.keep", { name });
}

onBeforeUnmount(() => merge.reset());
</script>

<template>
  <DrawerMenuContent
    :label="t('canvas:merge.title')"
    :description="t('canvas:merge.description')"
    actions
  >
    <!-- 1. Choix des versions -->
    <template v-if="step === 'select'">
      <div v-if="isLoading" class="py-4 text-center text-grey">
        <span class="spinner-border spinner-border-sm" />
      </div>
      <div
        v-else-if="activeBranches.length < 2"
        class="py-5 text-center text-grey fw-light"
      >
        <small>{{ t("canvas:merge.select.single") }}</small>
      </div>
      <div v-else class="d-flex flex-column gap-3">
        <label class="d-flex flex-column gap-1">
          <small class="text-grey">{{ t("canvas:merge.select.target") }}</small>
          <el-select v-model="targetId" popper-class="canvas-popper">
            <el-option
              v-for="b in activeBranches"
              :key="b.id"
              :label="b.name"
              :value="b.id"
            >
              <span class="branch-option">
                <span class="text-truncate">{{ b.name }}</span>
                <small class="branch-detail">{{ branchDetail(b) }}</small>
              </span>
            </el-option>
          </el-select>
          <small v-if="branchDetail(target)" class="branch-detail">
            {{ branchDetail(target) }}
          </small>
        </label>
        <label class="d-flex flex-column gap-1">
          <small class="text-grey">{{ t("canvas:merge.select.merged") }}</small>
          <el-select v-model="mergedId" popper-class="canvas-popper">
            <el-option
              v-for="b in activeBranches"
              :key="b.id"
              :label="b.name"
              :value="b.id"
              :disabled="b.id === targetId"
            >
              <span class="branch-option">
                <span class="text-truncate">{{ b.name }}</span>
                <small class="branch-detail">{{ branchDetail(b) }}</small>
              </span>
            </el-option>
          </el-select>
          <small v-if="branchDetail(merged)" class="branch-detail">
            {{ branchDetail(merged) }}
          </small>
        </label>
        <small class="text-grey">
          {{
            t("canvas:merge.select.hint", {
              target: target?.name ?? "",
              merged: merged?.name ?? "",
            })
          }}
        </small>
        <small v-if="deviceState.isOnline === false" class="text-warning">
          {{ t("canvas:merge.select.offline") }}
        </small>
      </div>
    </template>

    <!-- 2. Analyse / fusion en cours -->
    <div
      v-else-if="step === 'analyzing' || step === 'applying'"
      class="py-5 d-flex flex-column align-items-center gap-3 text-grey"
    >
      <span class="spinner-border" />
      <small>{{ t(`canvas:merge.progress.${step}`) }}</small>
      <small v-if="step === 'analyzing' && readProgress">
        {{ t("canvas:merge.progress.reading", readProgress) }}
      </small>
    </div>

    <!-- 3. Résolution des conflits -->
    <template v-else-if="step === 'resolve' && summary">
      <ul class="merge-summary">
        <li>{{ t("canvas:merge.summary.added", { count: summary.added }) }}</li>
        <li>
          {{ t("canvas:merge.summary.modified", { count: summary.modified }) }}
        </li>
        <li>
          {{ t("canvas:merge.summary.deleted", { count: summary.deleted }) }}
        </li>
        <li v-if="summary.observationLinks">
          {{
            t("canvas:merge.summary.observationLinks", {
              count: summary.observationLinks,
            })
          }}
        </li>
        <li v-for="[kind, count] in recordSummary" :key="kind">
          {{ t(`canvas:merge.summary.records.${kind}`, { count }) }}
        </li>
      </ul>

      <small v-if="sharedMarkers.length" class="d-block text-warning mb-2">
        {{
          t("canvas:merge.summary.sharedMarkers", {
            count: sharedMarkers.length,
            list: sharedMarkerList,
          })
        }}
      </small>

      <div v-if="!conflicts.length" class="py-3 text-center text-grey">
        <PhosphorIcon name="check-circle" :size="20" />
        <div>
          <small>{{ t("canvas:merge.resolve.none") }}</small>
        </div>
      </div>

      <template v-else>
        <small class="d-block text-warning mb-2">
          {{ t("canvas:merge.resolve.dontEdit") }}
        </small>

        <!-- Navigation entre conflits : le courant est montré sur le plan -->
        <div class="d-flex align-items-center gap-2 mb-2">
          <button
            type="button"
            class="nav-btn"
            :aria-label="t('canvas:merge.resolve.previous')"
            @click="merge.previous()"
          >
            <PhosphorIcon name="caret-left" :size="14" />
          </button>
          <small class="fw-medium">
            {{
              t("canvas:merge.resolve.position", {
                index: currentIndex + 1,
                count: conflicts.length,
              })
            }}
          </small>
          <button
            type="button"
            class="nav-btn"
            :aria-label="t('canvas:merge.resolve.next')"
            @click="merge.next()"
          >
            <PhosphorIcon name="caret-right" :size="14" />
          </button>
          <small class="text-grey ms-auto">
            {{ t("canvas:merge.resolve.remaining", { count: remaining }) }}
          </small>
        </div>

        <!-- Légende des couleurs, comme sur le plan -->
        <div class="d-flex flex-wrap gap-3 mb-2 legend">
          <span>
            <i class="dot" :style="{ background: mergeSideCss('target') }" />
            {{ targetName }}
          </span>
          <span>
            <i class="dot" :style="{ background: mergeSideCss('merged') }" />
            {{ mergedName }}
          </span>
        </div>

        <div class="d-flex gap-2 mb-3">
          <DRBtn
            size="xs"
            variant="outline-white"
            :label="t('canvas:merge.resolve.allTarget', { name: targetName })"
            @click="merge.resolveAll('target')"
          />
          <DRBtn
            size="xs"
            variant="outline-white"
            :label="t('canvas:merge.resolve.allMerged', { name: mergedName })"
            @click="merge.resolveAll('merged')"
          />
        </div>

        <div
          v-for="c in conflicts"
          :key="c.id"
          class="conflict-card"
          :class="{
            resolved: !!resolutions[c.id],
            current: c.id === currentConflictId,
          }"
          role="button"
          tabindex="0"
          @click="merge.select(c.id)"
          @keydown.enter="merge.select(c.id)"
        >
          <small class="d-block fw-medium text-truncate">
            {{ conflictTitle(c) }}
          </small>
          <small class="d-block text-grey">{{ conflictSentence(c) }}</small>
          <div class="d-flex flex-wrap gap-2 mt-2">
            <button
              v-for="choice in choicesOf(c)"
              :key="choice"
              type="button"
              class="choice-btn"
              :class="{ active: resolutions[c.id] === choice }"
              @click.stop="merge.resolve(c.id, choice)"
            >
              <i
                v-if="sideOf(choice)"
                class="dot"
                :style="{ background: mergeSideCss(sideOf(choice)!) }"
              />
              {{ choiceLabel(c, choice) }}
            </button>
          </div>
        </div>
      </template>
    </template>

    <!-- 4. Terminé -->
    <div v-else-if="step === 'done'" class="d-flex flex-column gap-2">
      <div class="text-center py-3">
        <PhosphorIcon name="check-circle" :size="28" />
        <div class="fw-medium mt-2">
          {{
            t("canvas:merge.done.title", {
              merged: merged?.name ?? "",
              target: target?.name ?? "",
            })
          }}
        </div>
      </div>
      <small v-if="result?.plan.detachedFromDeletedObservations">
        {{
          t("canvas:merge.done.detached", {
            count: result.plan.detachedFromDeletedObservations,
          })
        }}
      </small>
      <small v-if="result?.failedOps" class="text-warning">
        {{
          t("canvas:merge.done.failed", {
            count: result.failedOps,
          })
        }}
      </small>
    </div>

    <!-- Erreur -->
    <div v-else-if="step === 'error'" class="py-4 text-center">
      <PhosphorIcon name="warning" :size="24" />
      <div class="mt-2">
        <small>{{ t(`canvas:merge.errors.${errorKey ?? "failed"}`) }}</small>
      </div>
    </div>

    <template #actions>
      <DRBtn
        v-if="step === 'select'"
        class="w-100 mt-2"
        phosphor-icon="git-merge"
        variant="outline-white"
        :disabled="!canAnalyze"
        :label="t('canvas:merge.select.analyze')"
        @click="startAnalysis"
      />
      <template v-else-if="step === 'resolve'">
        <DRBtn
          class="w-100 mt-2"
          phosphor-icon="git-merge"
          variant="outline-white"
          :disabled="remaining > 0"
          :label="t('canvas:merge.resolve.apply')"
          @click="startMerge"
        />
        <DRBtn
          class="w-100 mt-2"
          variant="flat"
          :label="t('canvas:merge.back')"
          @click="merge.reset()"
        />
      </template>
      <DRBtn
        v-else-if="step === 'error'"
        class="w-100 mt-2"
        variant="outline-white"
        :label="t('canvas:merge.back')"
        @click="merge.reset()"
      />
      <DRBtn
        v-else-if="step === 'done'"
        class="w-100 mt-2"
        variant="outline-white"
        :label="t('close')"
        @click="emit('close')"
      />
    </template>
  </DrawerMenuContent>
</template>

<style lang="scss" scoped>
// Nom à gauche, dernière modification à droite (une ligne : hauteur d'option
// standard).
.branch-option {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.branch-detail {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11.5px;
  font-weight: 400;
}

.merge-summary {
  margin: 0 0 12px;
  padding-left: 18px;
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.8);
}

.conflict-card {
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid var(--canvas-border);
  border-left: 3px solid #ffc000;
  border-radius: 8px;

  cursor: pointer;

  &.resolved {
    border-left-color: #92d050;
  }

  // Conflit montré sur le plan
  &.current {
    border-color: rgba(255, 255, 255, 0.6);
    background-color: rgba(255, 255, 255, 0.04);
  }
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);

  &:hover {
    background-color: var(--canvas-btn-hover-bg);
  }
}

.legend {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

// Pastille de couleur d'une version (la même que ses fantômes sur le plan)
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: middle;
}

.choice-btn {
  display: inline-flex;
  align-items: center;
  min-height: 36px; // cible tactile
  padding: 0 12px;
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  background: transparent;
  color: #fff;
  font-size: 12.5px;

  &.active {
    border-color: #fff;
    background-color: rgba(255, 255, 255, 0.15);
    font-weight: 500;
  }
}
</style>
